import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react"
import * as React from "react"
import { DayPicker } from "react-day-picker"

import { cn } from "../../lib/utils"
import { buttonVariants } from "./button"

/**
 * El calendario del sistema, sobre react-day-picker.
 *
 * En JSX y no en TSX: acá no hay TypeScript en ningún lado, ni adelante ni
 * atrás. Las anotaciones del original (`CalendarProps`, los `as keyof typeof`)
 * se cayeron en la conversión; el resto es igual.
 *
 * El color sale de los tokens `--primary` y `--ring`, así que en la página
 * pública de un negocio toma su color de marca solo — `aplicarBranding` los
 * reescribe (ver lib/branding.js). Un `bg-[#...]` acá sería un calendario que
 * se salió del sistema.
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  components: userComponents,
  ...props
}) {
  const defaultClassNames = {
    months: "relative flex flex-col sm:flex-row gap-4",
    month: "w-full",
    month_caption: "relative mx-10 mb-1 flex h-9 items-center justify-center z-20",
    // `inline-flex items-center gap-1` no venía en el original y hace falta con
    // los desplegables: ahí adentro del label van el texto Y la flechita, y sin
    // esto la flecha caía en un renglón aparte, debajo de "agosto".
    caption_label: "inline-flex items-center gap-1 text-sm font-medium",
    nav: "absolute top-0 flex w-full justify-between z-10",
    button_previous: cn(
      buttonVariants({ variant: "ghost" }),
      "size-9 text-muted-foreground/80 hover:text-foreground p-0",
    ),
    button_next: cn(
      buttonVariants({ variant: "ghost" }),
      "size-9 text-muted-foreground/80 hover:text-foreground p-0",
    ),
    weekday: "size-9 p-0 text-xs font-medium text-muted-foreground/80",
    day_button:
      "relative flex size-9 items-center justify-center whitespace-nowrap rounded-lg p-0 text-foreground outline-offset-2 group-[[data-selected]:not(.range-middle)]:[transition-property:color,background-color,border-radius,box-shadow] group-[[data-selected]:not(.range-middle)]:duration-150 focus:outline-none group-data-[disabled]:pointer-events-none focus-visible:z-10 hover:bg-accent group-data-[selected]:bg-primary hover:text-foreground group-data-[selected]:text-primary-foreground group-data-[disabled]:text-foreground/30 group-data-[disabled]:line-through group-data-[outside]:text-foreground/30 group-data-[outside]:group-data-[selected]:text-primary-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 group-[.range-start:not(.range-end)]:rounded-e-none group-[.range-end:not(.range-start)]:rounded-s-none group-[.range-middle]:rounded-none group-data-[selected]:group-[.range-middle]:bg-accent group-data-[selected]:group-[.range-middle]:text-foreground",
    day: "group size-9 px-0 text-sm",
    range_start: "range-start",
    range_end: "range-end",
    range_middle: "range-middle",
    today:
      "*:after:pointer-events-none *:after:absolute *:after:bottom-1 *:after:start-1/2 *:after:z-10 *:after:size-[3px] *:after:-translate-x-1/2 *:after:rounded-full *:after:bg-primary [&[data-selected]:not(.range-middle)>*]:after:bg-background [&[data-disabled]>*]:after:bg-foreground/30 *:after:transition-colors",
    outside: "text-muted-foreground data-selected:bg-accent/50 data-selected:text-muted-foreground",
    hidden: "invisible",
    week_number: "size-9 p-0 text-xs font-medium text-muted-foreground/80",

    // Los tres de abajo no venían en el original, que sólo contempla la
    // cabecera con flechas. Con `captionLayout="dropdown"` react-day-picker
    // cambia el título por dos <select> (mes y año) y sin estas clases salen
    // con el estilo crudo del navegador, pegados y sin alinear con el resto.
    // Hace falta para cualquier fecha lejana —un cumpleaños— donde avanzar de a
    // un mes no es una opción.
    dropdowns: "flex items-center gap-1.5",
    dropdown_root: "relative inline-flex items-center",
    dropdown:
      "absolute inset-0 z-10 w-full cursor-pointer appearance-none opacity-0",
  }

  const mergedClassNames = Object.keys(defaultClassNames).reduce(
    (acc, key) => ({
      ...acc,
      [key]: classNames?.[key]
        ? cn(defaultClassNames[key], classNames[key])
        : defaultClassNames[key],
    }),
    {},
  )

  const defaultComponents = {
    Chevron: (props) => {
      if (props.orientation === "left") {
        return <ChevronLeft size={16} strokeWidth={2} {...props} aria-hidden="true" />
      }
      // `down` es el de los desplegables de mes y año. El original sólo
      // distinguía izquierda de derecha, así que al lado de "agosto" aparecía
      // una flecha apuntando a la derecha, como si fuera a pasar de mes.
      if (props.orientation === "down") {
        return <ChevronDown size={14} strokeWidth={2} {...props} aria-hidden="true" />
      }
      return <ChevronRight size={16} strokeWidth={2} {...props} aria-hidden="true" />
    },
  }

  const mergedComponents = {
    ...defaultComponents,
    ...userComponents,
  }

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("w-fit", className)}
      classNames={mergedClassNames}
      components={mergedComponents}
      {...props} />
  );
}
Calendar.displayName = "Calendar"

export { Calendar }
